import { useCallback, useRef, useState } from "react";
import debounce from "lodash-es/debounce";
import { useQuery } from "@apollo/client";
import { ActivityCategory } from "@onmo/graphql/graphql";
import { getActivitiesGQL } from "../graphql";
import { Activities } from "../models/activities";

const LIMIT_ACTIVITIES = 20;

export const useActivities = () => {
  const inFlightRef = useRef(false);
  const [hasMore, setHasMore] = useState(true);
  const prevCountRef = useRef(0);
  const { data, fetchMore, loading } = useQuery(getActivitiesGQL, {
    fetchPolicy: "network-only",
    variables: {
      input: {
        category: ActivityCategory.Profile,
        page: {
          limit: LIMIT_ACTIVITIES,
          offset: 0,
        },
        products: ["ca"],
      },
    },
    onCompleted(res) {
      const items = res?.me?.activities?.items ?? [];
      prevCountRef.current = items.length;
      const total = res?.me?.activities?.total;
      if (typeof total === "number") setHasMore(items.length < total);
    },
  });
  const activitiesByDate = Activities.groupActivitiesByStartedDate(
    data?.me?.activities?.items,
  );
  const loadMore = useCallback(async () => {
    if (inFlightRef.current || loading || !hasMore) return;
    inFlightRef.current = true;

    try {
      const currentCount = data?.me?.activities?.items?.length ?? 0;

      const res = await fetchMore({
        variables: {
          input: {
            category: ActivityCategory.Profile,
            page: {
              limit: LIMIT_ACTIVITIES,
              offset: currentCount,
            },
            products: ["ca"],
          },
        },
      });

      const newItems = res?.data?.me?.activities?.items ?? [];
      const afterCount = newItems.length;

      if (afterCount === prevCountRef.current) {
        setHasMore(false);
      } else {
        prevCountRef.current = afterCount;
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [data, fetchMore, hasMore, loading]);
  const loadMoreActivity = debounce(loadMore, 300);

  return {
    activitiesByDate,
    loadMoreActivity,
    loading,
  };
};
